import { Box, FormControl } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ReactElement, memo, useCallback, useMemo } from 'react'
import { IEntityStatusFilter, ITableFilters } from '../interfaces'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { setFilterList } from '../../../redux/slices/entitySlice'
import MultiSelectInput from '../../Common/MultiSelectInput'
import { getEntityTableEntityStatusFilter } from '../../../utils/EntityUtils'
import {
    DeviceManagerStatusToUI,
    EntityStatusBEtoUI,
} from '../../../constants/constants'

interface IEntityStatusProps {
    templateId: string
    applicationId?: string
    tabIndex?: string
    label?: string
    isDeviceManager?: boolean
}
const EntityStatusFilter = ({
    templateId,
    applicationId,
    tabIndex,
    label,
    isDeviceManager,
}: IEntityStatusProps): ReactElement => {
    const { t } = useTranslation()
    const allTableFilters = useAppSelector(
        (state) => state.entity.allTableFilters,
    )
    const dispatch = useAppDispatch()
    const entityStatusFilter: IEntityStatusFilter | undefined =
        getEntityTableEntityStatusFilter(
            allTableFilters,
            templateId,
            applicationId,
            tabIndex,
        )
    const value: string[] = entityStatusFilter?.values || []

    const handleChange = useCallback(
        (_id: string, updatedSelections: string[]) => {
            const updatedAllTableFilters = allTableFilters.map((filter) => {
                if (
                    filter.applicationId === applicationId &&
                    filter.tabIndex === tabIndex &&
                    filter.entityTemplateId === templateId
                ) {
                    return {
                        ...filter,
                        entityStatusFilter: {
                            type: 'status',
                            values: updatedSelections,
                        },
                    } as ITableFilters
                }
                return filter
            })
            dispatch(setFilterList(updatedAllTableFilters))
        },
        [allTableFilters, applicationId, tabIndex, templateId, dispatch],
    )

    const menuList = useMemo(
        () =>
            Object.entries(
                isDeviceManager ? DeviceManagerStatusToUI : EntityStatusBEtoUI,
            ).map(([id, name]) => ({ id, name: t(name) })),
        [t, isDeviceManager],
    )

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth size="small">
                <MultiSelectInput
                    id="entity-status-select"
                    label={label ? t(label) : t('entityStatus')}
                    isLegend={true}
                    value={value}
                    onChange={handleChange}
                    list={menuList}
                    displayInputLabel={false}
                    renderLabelInValue={true}
                    displayEmpty={true}
                />
            </FormControl>
        </Box>
    )
}

const MemoizedEntityStatus = memo(EntityStatusFilter)
MemoizedEntityStatus.displayName = 'EntityStatus'
export default MemoizedEntityStatus
